import React, { useState, useRef, useEffect } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Legend } from 'recharts';
import Header from '../Admin/header/Header';
import Sidebar from '../Admin/Sidebar/Sidebar';
import { Search, UserPlus, X } from 'lucide-react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import './dashboard.css';

const Dashboard = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const searchContainerRef = useRef(null);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setIsSearchFocused(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const attendanceData = [
        { name: 'On Leave', value: 25 },
        { name: 'Working Today', value: 75 },
    ];

    const pharmacyData = [
        { Medicine: 4000, OtherItems: 2400 },
        { Medicine: 3000, OtherItems: 1398 },
        { Medicine: 2000, OtherItems: 9800 },
        { Medicine: 2780, OtherItems: 3908 },
        { Medicine: 1890, OtherItems: 4800 },
    ];

    const COLORS = ['#0179B0', '#B2E2ED'];

    return (
        <div className="dashboard">
            <Header />
            <div className="dashboard-content">
                <Sidebar />
                <main className="main-content">
                    <div className="top-bar">
                        <Box
                            ref={searchContainerRef}
                            sx={{
                                display: "flex",
                                backgroundColor: "white",
                                border: isSearchFocused ? "2px solid #0179B0" : "1px solid #e0e0e0",
                                borderRadius: "8px",
                                alignItems: "center",
                                position: "relative",
                                boxShadow: isSearchFocused 
                                    ? "0 4px 12px rgba(1, 121, 176, 0.15)" 
                                    : "0 2px 4px rgba(0, 0, 0, 0.05)",
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                "&:hover": {
                                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                                    borderColor: "#c0c0c0"
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    padding: "12px 16px",
                                    width: "300px",
                                    transition: "all 0.3s ease",
                                    gap: "10px"
                                }}
                            >
                                <Search 
                                    size={20} 
                                    style={{ 
                                        color: isSearchFocused ? "#0179B0" : "#666",
                                        transition: "color 0.3s ease",
                                        flexShrink: 0
                                    }} 
                                />
                                <input
                                    type="text"
                                    placeholder="Search by UHID"
                                    value={search || ""}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onFocus={() => setIsSearchFocused(true)}
                                    style={{
                                        border: "none",
                                        outline: "none",
                                        background: "transparent",
                                        width: "100%",
                                        fontSize: "14px",
                                        color: "#333",
                                        padding: "0",
                                        minHeight: "24px",
                                        touchAction: "manipulation",
                                        WebkitTapHighlightColor: "transparent"
                                    }}
                                />
                                {search && (
                                    <X 
                                        size={18} 
                                        style={{ 
                                            cursor: "pointer", 
                                            color: "#999",
                                            flexShrink: 0,
                                            padding: "2px",
                                            transition: "all 0.2s ease",
                                            touchAction: "manipulation"
                                        }}
                                        onClick={() => setSearch("")}
                                        onMouseEnter={(e) => e.target.style.color = "#0179B0"}
                                        onMouseLeave={(e) => e.target.style.color = "#999"}
                                    />
                                )}
                            </Box>
                        </Box>
                        <button
                            className="patient-registration"
                            onClick={() => navigate("/patient/form")}
                        >
                            <UserPlus size={20} />
                            Patient Registration
                        </button>
                    </div>
                    <div className="charts">
                        <div className="chart-section">
                            <h2>User Attendance</h2>
                            <div className="chart-container attendance-chart">
                                <div className="chart-content">
                                    <PieChart width={300} height={255}>
                                        <Pie
                                            data={attendanceData}
                                            cx={150}
                                            cy={150}
                                            innerRadius={2}
                                            outerRadius={100}
                                            startAngle={90}
                                            endAngle={-270}
                                            paddingAngle={10}
                                            dataKey="value"
                                        >
                                            {attendanceData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                    <div className="chart-legend">
                                        <div className="legend-item">
                                            <span className="legend-percentage" style={{ color: COLORS[0] }}>{attendanceData[0].value}%</span>
                                            <span className="legend-label">{attendanceData[0].name}</span>
                                        </div>
                                        <div className="legend-item">
                                            <span className="legend-percentage" style={{ color: COLORS[1] }}>{attendanceData[1].value}%</span>
                                            <span className="legend-label">{attendanceData[1].name}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="chart-section">
                            <h2>Pharmacy Sales</h2>
                            <div className="chart-container pharmacy-chart">
                                <div className="chart-content">
                                    <BarChart width={450} height={320} data={pharmacyData} barGap={5}>
                                        <XAxis axisLine={false} tickLine={false} tick={false} />
                                        <YAxis axisLine={false} tickLine={false} tick={false} />
                                        <Bar dataKey="Medicine" fill="#8B9FE5" />
                                        <Bar dataKey="OtherItems" fill="#B3FFFE" />
                                    </BarChart>
                                    <div className="pharmacy-legend">
                                        <div className="legend-item">
                                            <span className="legend-color" style={{ backgroundColor: '#8B9FE5' }}></span>
                                            <span className="legend-label">Medicine</span>
                                        </div>
                                        <div className="legend-item">
                                            <span className="legend-color" style={{ backgroundColor: '#B3FFFE' }}></span>
                                            <span className="legend-label">Other Items</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bottom-section">
                        <div className="reminders">
                            <div className="content-box">
                                <h2>Reminders <span className="see-more">See More</span></h2>
                                <div className="box-border1">
                                    <ul className="reminders-list">
                                        <li>Follow-Up With Patient John Doe: Scheduled For September 14, 2024, At 10:00 AM</li>
                                        <li>Medication Stock Check (Pharmacy): Due By September 14, 2024, At 2:00 PM</li>
                                        <li>Team Meeting (Nursing Dept): Scheduled For September 15, 2024, At 11:00 AM In Room 1A</li>
                                    </ul>
                                    <button className="create-reminder">Create Reminder</button>
                                </div>
                            </div>
                        </div>
                        <div className="news-updates">
                            <div className="content-box">
                                <h2>News Updates <span className="see-more">See More</span></h2>
                                <div className="box-border2">
                                    <div className="news-item">
                                        <div className="image">
                                            <img src="/Images/robotics.png" alt="Robotic Surgery" />
                                        </div>
                                        <div>
                                            <h3>Successful Completion Of First Robotic-Assisted Surgery At City Hospital</h3>
                                            <a href="#" className="read-article">Read Article</a>
                                        </div>
                                    </div>
                                    <div className="news-item">
                                        <div className="image">
                                            <img src="/Images/surgery.png" alt="Heart Surgery" />
                                        </div>
                                        <div>
                                            <h3>City Hospital Performs Groundbreaking Heart Surgery with New Technique</h3>
                                            <a href="#" className="read-article">Read Article</a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Dashboard;